import { useCallback, useEffect, useRef } from 'react';
import { CELL_WIDTH, ROW_HEIGHT } from '../utils/gridConstants';

/**
 * Custom hook for keyboard navigation in the planner grid.
 * Provides accessible keyboard controls for:
 * - Arrow keys: Navigate between blocks
 * - Shift+Arrow: Move selected block
 * - Enter: Start editing block name
 * - Escape: Cancel editing / deselect
 * - Delete/Backspace: Delete selected block
 * - Tab: Navigate to next/previous block
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.blocks - Array of block objects
 * @param {string|number|null} options.selectedBlockId - Currently selected block ID
 * @param {string|number|null} options.editingBlockId - Currently editing block ID
 * @param {Map} options.weekIndexMap - Map of week string to column index
 * @param {Array} options.weeks - Array of week strings
 * @param {number} options.maxRows - Maximum row number
 * @param {boolean} options.isOnline - Whether the app is online
 * @param {Function} options.onSelect - Callback when block is selected
 * @param {Function} options.onDeselect - Callback when block is deselected
 * @param {Function} options.onStartEditing - Callback to start editing
 * @param {Function} options.onStopEditing - Callback to stop editing
 * @param {Function} options.onDelete - Callback to delete block
 * @param {Function} options.onMove - Callback to move block (blockId, newCol, newRow, weekSpan)
 * @param {React.RefObject} options.containerRef - Ref to the grid container
 * @returns {Object} Keyboard navigation handlers and focus management
 */
const useGridNavigation = ({
  blocks,
  selectedBlockId,
  editingBlockId,
  weekIndexMap,
  weeks,
  maxRows,
  isOnline = true,
  onSelect,
  onDeselect,
  onStartEditing,
  onStopEditing,
  onDelete,
  onMove,
  containerRef,
}) => {
  const lastFocusedBlockRef = useRef(null);

  /**
   * Get block at specific grid position (row, col)
   */
  const getBlockAtPosition = useCallback((row, col) => blocks.find((block) => {
    const fromIndex = weekIndexMap.get(block.fromWeek);
    const toIndex = weekIndexMap.get(block.toWeek);
    if (fromIndex === undefined || toIndex === undefined) return false;
    return block.row === row && col >= fromIndex && col <= toIndex;
  }), [blocks, weekIndexMap]);

  /**
   * Get currently selected block
   */
  const getSelectedBlock = useCallback(
    () => (selectedBlockId ? blocks.find((b) => b.id === selectedBlockId) : null),
    [blocks, selectedBlockId],
  );

  /**
   * Find next block in specified direction
   */
  const findNextBlock = useCallback((direction) => {
    const currentBlock = getSelectedBlock();
    if (!currentBlock) {
      // No block selected, select first visible block
      return blocks.length > 0 ? blocks[0] : null;
    }

    const currentFromIndex = weekIndexMap.get(currentBlock.fromWeek);
    const currentToIndex = weekIndexMap.get(currentBlock.toWeek);
    const currentMidCol = Math.floor((currentFromIndex + currentToIndex) / 2);
    const currentRow = currentBlock.row;

    let nextBlock = null;
    let bestDistance = Infinity;

    blocks.forEach((block) => {
      if (block.id === currentBlock.id) return;

      const fromIndex = weekIndexMap.get(block.fromWeek);
      const toIndex = weekIndexMap.get(block.toWeek);
      if (fromIndex === undefined || toIndex === undefined) return;

      const midCol = Math.floor((fromIndex + toIndex) / 2);
      const row = block.row;

      let isInDirection = false;
      let distance = 0;

      switch (direction) {
        case 'left':
          isInDirection = toIndex < currentFromIndex
            || (row !== currentRow && midCol < currentMidCol);
          if (isInDirection) {
            distance = Math.abs(currentFromIndex - toIndex) + Math.abs(currentRow - row) * weeks.length;
          }
          break;
        case 'right':
          isInDirection = fromIndex > currentToIndex
            || (row !== currentRow && midCol > currentMidCol);
          if (isInDirection) {
            distance = Math.abs(fromIndex - currentToIndex) + Math.abs(currentRow - row) * weeks.length;
          }
          break;
        case 'up':
          isInDirection = row < currentRow;
          if (isInDirection) {
            distance = (currentRow - row) * weeks.length + Math.abs(midCol - currentMidCol);
          }
          break;
        case 'down':
          isInDirection = row > currentRow;
          if (isInDirection) {
            distance = (row - currentRow) * weeks.length + Math.abs(midCol - currentMidCol);
          }
          break;
        default:
          break;
      }

      if (isInDirection && distance < bestDistance) {
        bestDistance = distance;
        nextBlock = block;
      }
    });

    return nextBlock;
  }, [getSelectedBlock, blocks, weekIndexMap, weeks.length]);

  /**
   * Move selected block by delta columns and rows
   */
  const moveSelectedBlock = useCallback((deltaCol, deltaRow) => {
    if (!isOnline || !onMove) return;

    const currentBlock = getSelectedBlock();
    if (!currentBlock) return;

    const fromIndex = weekIndexMap.get(currentBlock.fromWeek);
    const toIndex = weekIndexMap.get(currentBlock.toWeek);
    if (fromIndex === undefined || toIndex === undefined) return;

    const weekSpan = toIndex - fromIndex + 1;
    const newCol = Math.max(0, Math.min(weeks.length - weekSpan, fromIndex + deltaCol));
    const newRow = Math.max(1, Math.min(maxRows, currentBlock.row + deltaRow));

    // Only move if position changed
    if (newCol !== fromIndex || newRow !== currentBlock.row) {
      onMove(currentBlock.id, newCol, newRow, weekSpan);
    }
  }, [isOnline, onMove, getSelectedBlock, weekIndexMap, weeks.length, maxRows]);

  /**
   * Scroll block into view
   */
  const scrollBlockIntoView = useCallback((block) => {
    if (!containerRef?.current || !block) return;

    const container = containerRef.current;
    const fromIndex = weekIndexMap.get(block.fromWeek);
    if (fromIndex === undefined) return;

    const blockLeft = fromIndex * CELL_WIDTH;
    const blockTop = (block.row - 1) * ROW_HEIGHT;
    const toIndex = weekIndexMap.get(block.toWeek) || fromIndex;
    const blockRight = (toIndex + 1) * CELL_WIDTH;
    const blockBottom = block.row * ROW_HEIGHT;

    const { scrollLeft, scrollTop, clientWidth, clientHeight } = container;

    let newScrollLeft = scrollLeft;
    let newScrollTop = scrollTop;

    // Horizontal scroll
    if (blockLeft < scrollLeft) {
      newScrollLeft = blockLeft - CELL_WIDTH; // Add margin
    } else if (blockRight > scrollLeft + clientWidth) {
      newScrollLeft = blockRight - clientWidth + CELL_WIDTH;
    }

    // Vertical scroll (accounting for header)
    const headerHeight = 56; // WeekHeader height
    const adjustedScrollTop = scrollTop + headerHeight;
    if (blockTop < adjustedScrollTop) {
      newScrollTop = blockTop - headerHeight - ROW_HEIGHT;
    } else if (blockBottom > scrollTop + clientHeight) {
      newScrollTop = blockBottom - clientHeight + ROW_HEIGHT;
    }

    if (newScrollLeft !== scrollLeft || newScrollTop !== scrollTop) {
      container.scrollTo({
        left: Math.max(0, newScrollLeft),
        top: Math.max(0, newScrollTop),
        behavior: 'smooth',
      });
    }
  }, [containerRef, weekIndexMap]);

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback((event) => {
    // Don't handle if we're in an input field (unless it's the block name input)
    if (
      event.target.tagName === 'INPUT'
      || event.target.tagName === 'TEXTAREA'
    ) {
      // Allow Escape to exit editing
      if (event.key === 'Escape' && editingBlockId) {
        event.preventDefault();
        if (onStopEditing) onStopEditing();
      }
      return;
    }

    const { key, shiftKey, ctrlKey, metaKey } = event;

    // Modifier key shortcuts
    const isModifier = ctrlKey || metaKey;

    switch (key) {
      case 'ArrowLeft':
        event.preventDefault();
        if (shiftKey && selectedBlockId) {
          // Shift+Arrow: Move block
          moveSelectedBlock(-1, 0);
        } else {
          // Arrow: Navigate to next block
          const leftBlock = findNextBlock('left');
          if (leftBlock) {
            if (onSelect) onSelect(leftBlock.id);
            scrollBlockIntoView(leftBlock);
          }
        }
        break;

      case 'ArrowRight':
        event.preventDefault();
        if (shiftKey && selectedBlockId) {
          moveSelectedBlock(1, 0);
        } else {
          const rightBlock = findNextBlock('right');
          if (rightBlock) {
            if (onSelect) onSelect(rightBlock.id);
            scrollBlockIntoView(rightBlock);
          }
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (shiftKey && selectedBlockId) {
          moveSelectedBlock(0, -1);
        } else {
          const upBlock = findNextBlock('up');
          if (upBlock) {
            if (onSelect) onSelect(upBlock.id);
            scrollBlockIntoView(upBlock);
          }
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (shiftKey && selectedBlockId) {
          moveSelectedBlock(0, 1);
        } else {
          const downBlock = findNextBlock('down');
          if (downBlock) {
            if (onSelect) onSelect(downBlock.id);
            scrollBlockIntoView(downBlock);
          }
        }
        break;

      case 'Enter':
        event.preventDefault();
        if (selectedBlockId && !editingBlockId && isOnline) {
          if (onStartEditing) onStartEditing(selectedBlockId);
        }
        break;

      case 'Escape':
        event.preventDefault();
        if (editingBlockId) {
          if (onStopEditing) onStopEditing();
        } else if (selectedBlockId) {
          if (onDeselect) onDeselect();
        }
        break;

      case 'Delete':
      case 'Backspace':
        // Only handle if not in editing mode and a block is selected
        if (!editingBlockId && selectedBlockId && isOnline) {
          event.preventDefault();
          if (onDelete) onDelete(selectedBlockId);
        }
        break;

      case 'Tab':
        // Tab through blocks
        if (!editingBlockId) {
          event.preventDefault();
          const direction = shiftKey ? 'left' : 'right';
          const nextBlock = findNextBlock(direction);
          if (nextBlock) {
            if (onSelect) onSelect(nextBlock.id);
            scrollBlockIntoView(nextBlock);
          }
        }
        break;

      case 'Home':
        if (isModifier) {
          // Ctrl+Home: Go to first block
          event.preventDefault();
          const firstBlock = blocks.reduce((min, block) => {
            const fromIndex = weekIndexMap.get(block.fromWeek);
            const minFromIndex = weekIndexMap.get(min?.fromWeek);
            if (fromIndex === undefined) return min;
            if (!min || (minFromIndex !== undefined && fromIndex < minFromIndex)) {
              return block;
            }
            return min;
          }, null);
          if (firstBlock) {
            if (onSelect) onSelect(firstBlock.id);
            scrollBlockIntoView(firstBlock);
          }
        }
        break;

      case 'End':
        if (isModifier) {
          // Ctrl+End: Go to last block
          event.preventDefault();
          const lastBlock = blocks.reduce((max, block) => {
            const toIndex = weekIndexMap.get(block.toWeek);
            const maxToIndex = weekIndexMap.get(max?.toWeek);
            if (toIndex === undefined) return max;
            if (!max || (maxToIndex !== undefined && toIndex > maxToIndex)) {
              return block;
            }
            return max;
          }, null);
          if (lastBlock) {
            if (onSelect) onSelect(lastBlock.id);
            scrollBlockIntoView(lastBlock);
          }
        }
        break;

      default:
        break;
    }
  }, [
    selectedBlockId,
    editingBlockId,
    isOnline,
    blocks,
    weekIndexMap,
    findNextBlock,
    moveSelectedBlock,
    scrollBlockIntoView,
    onSelect,
    onDeselect,
    onStartEditing,
    onStopEditing,
    onDelete,
  ]);

  // Add keyboard listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Track last focused block for focus restoration
  useEffect(() => {
    if (selectedBlockId) {
      lastFocusedBlockRef.current = selectedBlockId;
    }
  }, [selectedBlockId]);

  return {
    // Handlers for manual invocation if needed
    handleKeyDown,
    findNextBlock,
    moveSelectedBlock,
    scrollBlockIntoView,

    // Focus management
    lastFocusedBlockRef,
  };
};

export default useGridNavigation;
